import { useEffect, useRef, useState } from "react";

/**
 * useLiveLaunch — subscribes to `/api/launches/{id}/live` SSE stream and
 * returns { live, updatedAt, connected }.
 * `live` is a partial launch object with the freshest server-sourced fields:
 *   { status, net, holdreason, failreason, webcast_live, probability }
 * Falls back gracefully if SSE isn't supported.
 */
export default function useLiveLaunch(id, apiBase) {
  const [state, setState] = useState({ live: null, updatedAt: null, connected: false });
  const esRef = useRef(null);

  useEffect(() => {
    if (!id || typeof EventSource === "undefined") return;
    const es = new EventSource(`${apiBase}/launches/${id}/live`);
    esRef.current = es;

    es.addEventListener("open", () => setState((s) => ({ ...s, connected: true })));

    es.addEventListener("update", (e) => {
      try {
        const patch = JSON.parse(e.data);
        setState({ live: patch, updatedAt: Date.now(), connected: true });
      } catch { /* ignore malformed */ }
    });

    es.addEventListener("error", () => {
      setState((s) => ({ ...s, connected: false }));
      // EventSource auto-reconnects; if closed permanently, drop it
      if (es.readyState === EventSource.CLOSED) {
        es.close();
      }
    });

    return () => { es.close(); esRef.current = null; };
  }, [id, apiBase]);

  return state;
}
